import {
    type ReactElement,
    isValidElement,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    ItemGroupContextType,
    ItemPartFlagProps,
    ItemMediaVariant,
    IItemGroupProps
} from "../../type";
import type IItemProps from "../../type";
import {
    PRESENCE_LIST_CHANGE_LIMIT
} from "../../../presenceList/util";
import {
    flattenCardChildren
} from "../../../card/util";
import {
    ItemGroupContext
} from "../../context";
import {
    getItemPartKind
} from "../../util";
import stylesheet, {
    getItemSeparatorInset
} from "../../stylesheet";
import PresenceList from "../../../presenceList";
import ItemSeparator from "../separator";

const getMediaVariant = (node: ReactElement<IItemProps>): ItemMediaVariant | undefined => {
    const media = flattenCardChildren(node.props.children).find((child) => getItemPartKind(child) === "media");

    if(!isValidElement<ItemPartFlagProps>(media)) {
        return undefined;
    }

    return media.props.variant ?? "plain";
};

const ItemGroup: FC<IItemGroupProps> = ({
    isDivided = false,
    variant = "plain",
    customTheme,
    children,
    style,
    size,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        avatarSizes,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        container: containerDynamicStyle
    } = useStyles({
        shapeConfigs,
        isDivided,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    const nodes = flattenCardChildren(children);

    const lastItemIndex = nodes.reduce((last, node, index) => getItemPartKind(node) === "item" ? index : last, -1);

    const firstItemIndex = nodes.findIndex((node) => getItemPartKind(node) === "item");

    const isWeb = Platform.OS === "web";

    const renderSeparator = (node: ReactElement<IItemProps>, isVisible: boolean) => {
        if(!isVisible) {
            return null;
        }

        return <ItemSeparator
            inset={getItemSeparatorInset({
                size: node.props.size ?? size ?? "medium",
                mediaVariant: getMediaVariant(node),
                controlHeights,
                touchTargets,
                avatarSizes,
                pointerType,
                iconSizes,
                spaces
            })}
            customTheme={customTheme}
        />;
    };

    const renderNode = (node: (typeof nodes)[number], index: number) => {
        if(getItemPartKind(node) !== "item" || !isValidElement<IItemProps>(node)) {
            return node;
        }

        const context: ItemGroupContextType = {
            isFirst: index === firstItemIndex,
            isLast: index === lastItemIndex,
            customTheme,
            variant,
            size
        };

        const isSeparatorVisible = isDivided && index !== firstItemIndex;

        return <ItemGroupContext.Provider
            key={node.key ?? index}
            value={context}
        >
            {renderSeparator(node, isSeparatorVisible)}
            {node}
        </ItemGroupContext.Provider>;
    };

    return <View
        {...props}
        role={props.role ?? (isWeb ? "list" : undefined)}
        style={[
            style,
            stylesheet.groupContainer,
            containerDynamicStyle
        ]}
    >
        <PresenceList
            gap={typeof containerDynamicStyle.gap === "number" ? containerDynamicStyle.gap : 0}
            animatedChangeLimit={PRESENCE_LIST_CHANGE_LIMIT}
            customTheme={customTheme}
        >
            {nodes.map(renderNode)}
        </PresenceList>
    </View>;
};
export default Object.assign(ItemGroup, {
    itemPart: "group" as const
});
